// fleet-generated: do not edit by hand
//
// Copied verbatim  : infra/fleet/artifacts/state-machines/app-boot-machine.ts
// Parameters       : none — this file is byte-identical across the fleet
// Destination      : packages/fleet/state-machines/src/app-boot-machine.ts
//
// TO CHANGE THIS FILE, edit the canonical copy in infra and re-render. It
// changes for the whole fleet at once. Editing it here is reverted by the
// next render, and the guard in this repo goes red in the meantime:
//
//     cd ../infra && node fleet/fleet.ts render --repo agape-church-app
//     node fleet/fleet.ts check --repo agape-church-app
//
// The generator runs LOCALLY and is never invoked by CI. CI only verifies
// that what is committed matches the hash in .github/fleet-lock.json, which
// also records WHICH infra commit produced it. The commit is deliberately
// not repeated here: one value, one place — and stamping it into every file
// meant a one-line template edit rewrote four files in 28 repos.
import { assign, setup } from "xstate";

// Settling delay to allow all parallel boot processes to fully stabilize
// before checking navigation state. Prevents race conditions between
// layout providers and navigation initialization.
const SETTLING_DELAY_MS = 20;

export type AppBootEvent =
  | { type: "FONTS_LOADED" }
  | { type: "DB_READY" }
  | { type: "I18N_READY" }
  | { type: "THEME_READY" }
  | { type: "TYPOGRAPHY_READY" }
  | { type: "SESSION_RESOLVED"; hasSession: boolean; inAuthGroup: boolean };

interface AppBootContext {
  fontsReady: boolean;
  dbReady: boolean;
  i18nReady: boolean;
  themeReady: boolean;
  typographyReady: boolean;
  sessionReady: boolean;
  hasSession: boolean;
  inAuthGroup: boolean;
}

export const appBootMachine = setup({
  // Schema Definition Pattern for XState v5 type inference
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  types: {} as {
    context: AppBootContext;
    events: AppBootEvent;
  },
}).createMachine({
  id: "appBoot",
  initial: "booting",
  context: {
    fontsReady: false,
    dbReady: false,
    i18nReady: false,
    themeReady: false,
    typographyReady: false,
    sessionReady: false,
    hasSession: false,
    inAuthGroup: false,
  },
  states: {
    booting: {
      type: "parallel",
      states: {
        fonts: {
          initial: "loading",
          states: {
            loading: {
              on: {
                FONTS_LOADED: "ready",
              },
            },
            ready: { type: "final" },
          },
        },
        database: {
          initial: "loading",
          states: {
            loading: {
              on: {
                DB_READY: {
                  target: "ready",
                  actions: assign({ dbReady: true }),
                },
              },
            },
            ready: { type: "final" },
          },
        },
        i18n: {
          initial: "loading",
          states: {
            loading: {
              on: {
                I18N_READY: {
                  target: "ready",
                  actions: assign({ i18nReady: true }),
                },
              },
            },
            ready: { type: "final" },
          },
        },
        theme: {
          initial: "loading",
          states: {
            loading: {
              on: {
                THEME_READY: {
                  target: "ready",
                  actions: assign({ themeReady: true }),
                },
              },
            },
            ready: { type: "final" },
          },
        },
        typography: {
          initial: "loading",
          states: {
            loading: {
              on: {
                TYPOGRAPHY_READY: {
                  target: "ready",
                  actions: assign({ typographyReady: true }),
                },
              },
            },
            ready: { type: "final" },
          },
        },
        session: {
          initial: "loading",
          states: {
            loading: {
              on: {
                SESSION_RESOLVED: {
                  target: "ready",
                  actions: assign({
                    hasSession: ({ event }) => event.hasSession,
                    inAuthGroup: ({ event }) => event.inAuthGroup,
                    sessionReady: true,
                  }),
                },
              },
            },
            ready: { type: "final" },
          },
        },
      },
      onDone: "settling",
    },

    settling: {
      after: {
        [SETTLING_DELAY_MS]: { target: "checkingNavigation" },
      },
    },

    checkingNavigation: {
      always: [
        {
          guard: ({ context }) => context.hasSession && context.inAuthGroup,
          target: "redirecting",
        },
        { target: "ready" },
      ],
    },

    redirecting: {
      on: {
        SESSION_RESOLVED: {
          actions: assign({
            hasSession: ({ event }) => event.hasSession,
            inAuthGroup: ({ event }) => event.inAuthGroup,
          }),
          target: "checkingNavigation",
          reenter: true,
        },
      },
    },

    ready: {
      on: {
        SESSION_RESOLVED: {
          actions: assign({
            hasSession: ({ event }) => event.hasSession,
            inAuthGroup: ({ event }) => event.inAuthGroup,
          }),
          target: "checkingNavigation",
          reenter: true,
        },
      },
    },
  },
});
